import React, { Component, ErrorInfo, ReactNode } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { useT } from '../../i18n'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary] Uncaught error:', error, info)
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      return <ErrorFallback message={this.state.error?.message} onRetry={() => this.setState({ hasError: false, error: null })} />
    }

    return this.props.children
  }
}

/** What a page that failed shows (a function, to follow the language) */
function ErrorFallback({ message, onRetry }: { message?: string; onRetry: () => void }) {
  const t = useT()
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: 300, padding: 32,
    }}>
      <div style={{
        background: 'rgba(239,68,68,0.08)',
        border: '1px solid rgba(239,68,68,0.2)',
        borderRadius: 12, padding: '32px 40px',
        textAlign: 'center', maxWidth: 480,
      }}>
        <AlertTriangle size={40} color="#F87171" style={{ marginBottom: 16 }} />
        <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: 'var(--fg)' }}>
          {t('common.errorTitle')}
        </h2>
        <p style={{ fontSize: 13, color: 'var(--fg-2)', marginBottom: 20, lineHeight: 1.6 }}>
          {message ?? t('common.errorUnexpected')}
        </p>
        <button
          onClick={onRetry}
          style={{
            background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)',
            color: 'var(--err)', borderRadius: 8, padding: '8px 18px',
            cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
          }}
        >
          <RefreshCw size={14} /> {t('common.tryAgainButton')}
        </button>
      </div>
    </div>
  )
}

// ─── Page-level loading placeholder ──────────────────────────────────────────

export function PageLoader() {
  const t = useT()
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
      <div style={{ textAlign: 'center' }}>
        <div className="spinner" style={{ margin: '0 auto 12px' }} />
        <div style={{ fontSize: 13, color: 'var(--fg-3)' }}>{t('common.loading')}</div>
      </div>
    </div>
  )
}

// ─── Inline error state ───────────────────────────────────────────────────────

export function InlineError({ message, onRetry }: { message?: string; onRetry?: () => void }) {
  const t = useT()
  return (
    <div style={{
      background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.15)',
      borderRadius: 8, padding: '14px 18px',
      display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <AlertTriangle size={16} color="#F87171" />
      <span style={{ fontSize: 13, flex: 1, color: 'var(--err)' }}>
        {message ?? t('common.loadFailed')}
      </span>
      {onRetry && (
        <button
          onClick={onRetry}
          style={{
            background: 'none', border: '1px solid rgba(248,113,113,0.3)',
            color: 'var(--err)', borderRadius: 6, padding: '4px 10px',
            cursor: 'pointer', fontSize: 12, fontFamily: 'inherit',
          }}
        >
          {t('common.retry')}
        </button>
      )}
    </div>
  )
}
